/**
 * Portal profile (tracker item 45): name, phone and the notification
 * opt-out. The email is shown but cannot be changed here (changing it needs
 * re-verification, which comes later); a password change goes through the
 * shared change-password screen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalProfile } from '../../api/types';
import { usePortal, usePortalApi } from './PortalContext';
import { usePortalLoad } from './usePortalLoad';
import { FailureBlock, InlineAlert, LoadingBlock } from './PortalStates';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import { describePortalFailure } from '../../utils/portal';

function ProfileForm({ profile, onSaved }: { profile: PortalProfile; onSaved: (next: PortalProfile) => void }) {
  const api = usePortalApi();
  const { refresh, reportFailure } = usePortal();
  const [name, setName] = useState(profile.name);
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [optOut, setOptOut] = useState(profile.notificationOptOut);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setName(profile.name);
    setPhone(profile.phone ?? '');
    setOptOut(profile.notificationOptOut);
  }, [profile]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setSaved(false);
    if (!name.trim()) return setError('Enter your name.');
    setBusy(true);
    try {
      const next = await api.updatePortalProfile({ name: name.trim(), phone: phone.trim() || null, notificationOptOut: optOut });
      onSaved(next);
      setSaved(true);
      void refresh();
    } catch (cause) {
      const described = describePortalFailure(cause, 'We could not save your changes.');
      setError(described.message);
      reportFailure(described);
    } finally {
      setBusy(false);
    }
  }

  const input = inputClass + ' min-h-[44px] text-base';
  return (
    <form onSubmit={(e) => void save(e)} className="space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200" noValidate>
      <div>
        <label htmlFor="profile-email" className={labelClass}>Email address</label>
        <input id="profile-email" className={input} value={profile.email} readOnly aria-readonly="true" aria-describedby="profile-email-hint" />
        <p id="profile-email-hint" className="mt-1 text-xs text-gray-500">To change your email address, please contact the office.</p>
      </div>
      <div>
        <label htmlFor="profile-name" className={labelClass}>Name</label>
        <input id="profile-name" className={input} value={name} onChange={(e) => setName(e.target.value)} disabled={busy} autoComplete="name" required />
      </div>
      <div>
        <label htmlFor="profile-phone" className={labelClass}>Phone</label>
        <input id="profile-phone" type="tel" className={input} value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy} autoComplete="tel" />
      </div>
      <label className="flex min-h-[44px] items-start gap-3 text-sm">
        <input type="checkbox" className="mt-0.5 h-5 w-5" checked={optOut} onChange={(e) => setOptOut(e.target.checked)} disabled={busy} />
        <span>
          <span className="font-medium">Do not send me reminders or other optional messages</span>
          <span className="block text-xs text-gray-600">You will still get receipts, invoices and account messages.</span>
        </span>
      </label>
      {error && <InlineAlert>{error}</InlineAlert>}
      {saved && <InlineAlert kind="success">Your changes were saved.</InlineAlert>}
      <button type="submit" className={`${primaryButtonClass} w-full text-base sm:w-auto`} disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
    </form>
  );
}

export function PortalProfilePage() {
  const api = usePortalApi();
  const load = useCallback(() => api.getPortalProfile(), [api]);
  const { data, loading, failure, reload, setData } = usePortalLoad(load, 'We could not load your profile.');

  if (loading && !data) return <LoadingBlock label="Loading profile" />;
  if (failure) return <FailureBlock failure={failure} onRetry={() => void reload()} />;
  if (!data) return null;
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold text-gray-900">Profile</h1>
      <p className="text-sm text-gray-600">Account: {data.customerName}</p>
      <ProfileForm profile={data} onSaved={setData} />
      <nav aria-label="Account" className="flex flex-wrap gap-x-2">
        <Link to="/portal/payment-methods" className="inline-flex min-h-[44px] items-center px-1 text-sm font-semibold text-blue-700 hover:underline">Payment methods</Link>
        <Link to="/change-password" className="inline-flex min-h-[44px] items-center px-1 text-sm font-semibold text-blue-700 hover:underline">Change password</Link>
      </nav>
    </div>
  );
}
